import { useState } from 'react';

/**
 * Formulário semeado com o que o servidor devolveu — e o que fazer quando o
 * servidor muda por baixo dele.
 *
 * ## O furo que este módulo fecha
 *
 * `useState(() => props.registro.campo)` semeia **uma vez**. A consulta que
 * alimenta a tela, porém, refaz sozinha: qualquer `invalidateQueries` de um
 * painel irmão da mesma ficha recarrega o registro inteiro. Depois disso os
 * campos continuam com o texto antigo, e gravar por cima apaga o que outra
 * pessoa escreveu no intervalo — sem 409 nenhum, porque a versão enviada é a
 * que acabou de chegar.
 *
 * ## A marca é o conteúdo, e não a versão da linha
 *
 * A pergunta que decide tudo é *"mudou o que eu estou editando?"*, e quem
 * responde melhor é uma **assinatura dos campos daquele formulário** — não a
 * coluna `version`.
 *
 * A diferença aparece na ficha de mudança, que tem cinco formulários sobre a
 * mesma linha. Avaliar o risco incrementa `ChangeDetail.version`; se a marca
 * fosse a versão, o painel de Planos — em que a pessoa estava escrevendo —
 * passaria a se declarar desatualizado por causa de uma ação **dela mesma**, no
 * painel de cima, e ofereceria descartar o texto. A assinatura não confunde as
 * duas coisas: o plano de retorno só está desatualizado quando o plano de
 * retorno mudou.
 *
 * ## As três situações
 *
 *   • a assinatura não mudou → **manter** o que está digitado;
 *   • mudou e ninguém digitou nada → **ressemear** em silêncio, que é o que se
 *     espera de uma tela que se atualiza sozinha;
 *   • mudou e há edição por cima → **avisar**, e travar a gravação. Descartar o
 *     texto de quem está escrevendo seria trocar um defeito por outro, e gravar
 *     por cima é exatamente o defeito que este módulo existe para fechar. Quem
 *     decide é a pessoa, no botão de descartar.
 */

export type DecisaoDeRascunho = 'manter' | 'ressemear' | 'avisar';

/**
 * A **marca** do registro: o que muda quando o servidor muda.
 *
 * Quase sempre uma assinatura de conteúdo (ver `assinatura`). Onde um
 * formulário é o único a editar a linha inteira — o diálogo da janela de
 * mudança, por exemplo —, a própria `version` serve.
 */
export type MarcaDeRegistro = string | number;

export function decidirRascunho(estado: {
  /** A marca de que o texto na tela foi copiado. */
  marcaSemeada: MarcaDeRegistro;
  /** A marca que a consulta acabou de trazer. */
  marcaAtual: MarcaDeRegistro;
  /** Houve edição local depois da semeadura. */
  sujo: boolean;
}): DecisaoDeRascunho {
  if (Object.is(estado.marcaAtual, estado.marcaSemeada)) return 'manter';
  return estado.sujo ? 'avisar' : 'ressemear';
}

/** O separador da assinatura: um caractere que não existe em texto digitado. */
const SEPARADOR = ' ';

/**
 * A assinatura de um punhado de campos.
 *
 * O separador não pode ser um caractere digitável: com uma barra, mover uma
 * letra de um campo para o outro produziria a mesma assinatura, e a alteração
 * passaria despercebida exatamente no caso em que ela importa.
 */
export function assinatura(
  ...partes: ReadonlyArray<string | number | boolean | null | undefined>
): string {
  return partes.map((parte) => String(parte ?? '')).join(SEPARADOR);
}

export interface Rascunho<T> {
  readonly valor: T;
  readonly definir: (proximo: T | ((atual: T) => T)) => void;
  readonly sujo: boolean;
  /** A marca de que este texto veio. */
  readonly marca: MarcaDeRegistro;
  /**
   * O que este formulário edita mudou no servidor e há edição local por cima.
   * Enquanto for verdade, gravar apagaria o trabalho da outra pessoa — o botão
   * de gravar fica desabilitado.
   */
  readonly desatualizado: boolean;
  /**
   * Gravou: o que está na tela passa a ser o que vale.
   *
   * Limpa o "sujo" e **mantém** o texto. Não ressemeia: no `onSuccess` a
   * consulta ainda não refez, e semear ali mostraria por um instante o texto de
   * antes da gravação — em rede lenta, tempo suficiente para alguém concluir
   * que o salvamento falhou e redigitar tudo. A ressemeadura acontece sozinha
   * quando a resposta nova chegar, com o mesmo conteúdo.
   */
  readonly confirmar: () => void;
  /** Joga fora o que foi digitado e volta ao que o servidor tem agora. */
  readonly redefinir: () => void;
}

/**
 * O rascunho de um registro que outra pessoa também pode estar editando.
 *
 * `semente` é chamada de novo a cada ressemeadura, então ela precisa ler o
 * registro corrente — passar um valor já calculado devolveria o texto velho.
 */
export function useRascunho<T>(semente: () => T, marcaAtual: MarcaDeRegistro): Rascunho<T> {
  const [estado, setEstado] = useState(() => ({
    valor: semente(),
    marca: marcaAtual,
    sujo: false,
  }));

  const decisao = decidirRascunho({
    marcaSemeada: estado.marca,
    marcaAtual,
    sujo: estado.sujo,
  });

  // `setState` durante a renderização é o padrão do React para estado derivado
  // de propriedade: ele descarta esta renderização e refaz na hora, antes de
  // qualquer pintura. Um `useEffect` faria a tela piscar com o texto velho.
  if (decisao === 'ressemear') {
    setEstado({ valor: semente(), marca: marcaAtual, sujo: false });
  }

  return {
    valor: estado.valor,
    marca: estado.marca,
    sujo: estado.sujo,
    desatualizado: decisao === 'avisar',
    definir: (proximo) =>
      setEstado((atual) => ({
        ...atual,
        valor:
          typeof proximo === 'function'
            ? (proximo as (anterior: T) => T)(atual.valor)
            : proximo,
        sujo: true,
      })),
    confirmar: () => setEstado((atual) => ({ ...atual, sujo: false })),
    redefinir: () => setEstado({ valor: semente(), marca: marcaAtual, sujo: false }),
  };
}
